import {
  type RemixiconComponentType,
  RiInboxArchiveLine,
  RiInboxUnarchiveLine,
  RiLeafLine,
  RiMoneyDollarBoxLine,
} from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import { appLinks } from 'src/lib/routing';
import { useCreateBill } from 'src/modules/accounting/bills/edit-bill/bill.api';
import { useCreateGrowerProduct } from 'src/modules/orders/growerProducts/api/create';
import { useCreatePurchaseOrder } from 'src/modules/orders/purchaseOrders/api/creates';
import { useCreateSalesOrder } from 'src/modules/orders/salesOrders/list/api/creates';

/**
 * Palette create-commands. Each fires the SAME mutation and is gated by the SAME
 * flag as its item in the header "Create new…" menu, so a palette-created record
 * is indistinguishable from a menu-created one and a member without the flag
 * never sees the command. Bill and grower-product creates surface failures via
 * `notifyUnlessSurfaced` (one toast: the Apollo error link's, else ours); a
 * silent create must never look like it succeeded.
 *
 *   command          flag
 *   ───────────────────────────────────────────────
 *   Sales Order      permissionsCanCreateSalesOrders
 *   Purchase Order   permissionsCanCreatePurchaseOrders
 *   Grower Product   workOrders
 *   Invoice          accounting
 *   Payable          accounting
 */
export interface CommandAction {
  id: string;
  label: string;
  /** Keywords the query matches against, in addition to the label. */
  keywords: string;
  icon: RemixiconComponentType;
  run: () => void;
}

export function useCommandActions(): CommandAction[] {
  const flags = useFlags();
  const navigate = useNavigate();
  const createSalesOrder = useCreateSalesOrder();
  const createPurchaseOrder = useCreatePurchaseOrder();
  const createGrowerProduct = useCreateGrowerProduct({
    onError: (error) => notifyUnlessSurfaced(error, 'Could not create the grower product.'),
  });
  const createBill = useCreateBill();

  const actions: CommandAction[] = [];

  if (flags.permissionsCanCreateSalesOrders) {
    actions.push({
      id: 'new-sales-order',
      label: 'New Sales Order',
      keywords: 'create sales order so',
      icon: RiInboxUnarchiveLine,
      run: () => createSalesOrder(),
    });
  }

  if (flags.permissionsCanCreatePurchaseOrders) {
    actions.push({
      id: 'new-purchase-order',
      label: 'New Purchase Order',
      keywords: 'create purchase order po',
      icon: RiInboxArchiveLine,
      run: () => createPurchaseOrder(),
    });
  }

  if (flags.workOrders) {
    actions.push({
      id: 'new-grower-product',
      label: 'New Grower Product',
      keywords: 'create grower product work order',
      icon: RiLeafLine,
      run: () => createGrowerProduct(),
    });
  }

  if (flags.accounting) {
    actions.push({
      id: 'new-invoice',
      label: 'New Invoice',
      keywords: 'create invoice bill',
      icon: RiMoneyDollarBoxLine,
      run: () => {
        createBill('INVOICE')
          .then((bill) => navigate(appLinks.bill({ slug: bill.slug })))
          .catch((error) => notifyUnlessSurfaced(error, 'Could not create the invoice.'));
      },
    });

    actions.push({
      id: 'new-payable',
      label: 'New Payable',
      keywords: 'create payable bill',
      icon: RiMoneyDollarBoxLine,
      run: () => {
        createBill('PAYABLE')
          .then((bill) => navigate(appLinks.bill({ slug: bill.slug })))
          .catch((error) => notifyUnlessSurfaced(error, 'Could not create the payable.'));
      },
    });
  }

  return actions;
}

/** Case-insensitive match of a command against the typed query (label + keywords). */
export function matchesCommand(action: CommandAction, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return `${action.label} ${action.keywords}`.toLowerCase().includes(needle);
}
